import { useState } from 'react'
import {
  Alert, Badge, Button, Card, Group, NumberInput, PasswordInput, Skeleton,
  Stack, Switch, Tabs, Text, TextInput, Title,
} from '@mantine/core'
import {
  IconAlertTriangle, IconDatabase, IconMail, IconSettings, IconTestPipe,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCan } from '@/store/auth'
import {
  settingsApi, type ConfigItem, type SecretStatus,
} from '@/api/settings'

const SOURCE_COLOR: Record<string, string> = { db: 'teal', env: 'blue', default: 'gray' }
const SOURCE_LABEL: Record<string, string> = { db: 'Salvo', env: 'Ambiente', default: 'Padrão' }

function err(e: unknown, fallback: string): string {
  const axiosErr = e as { response?: { data?: { detail?: string } } }
  return axiosErr?.response?.data?.detail ?? fallback
}

function ConfigRow({ item }: { item: ConfigItem }) {
  const qc = useQueryClient()
  const [val, setVal] = useState<unknown>(item.value)
  // "sujo" = valor editado difere do persistido (item.value). Coerção por string
  // cobre bool/int/string. Após salvar, o refetch atualiza item.value == val →
  // volta a ficar limpo → botão desabilita de novo.
  const dirty = String(val ?? '') !== String(item.value ?? '')
  const save = useMutation({
    mutationFn: () => settingsApi.updateConfig(item.key, val),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      notifications.show({ color: 'teal', title: 'Salvo', message: `${item.key} atualizado.` })
    },
    onError: (e: unknown) => notifications.show({ color: 'red', title: 'Erro', message: err(e, 'Falha ao salvar.') }),
  })
  return (
    <Card withBorder padding="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text size="sm" fw={600}>{item.key}</Text>
            <Badge size="xs" variant="light" color={SOURCE_COLOR[item.source]}>{SOURCE_LABEL[item.source]}</Badge>
            <Badge size="xs" variant="outline" color={item.apply_mode === 'restart' ? 'orange' : 'green'}>
              {item.apply_mode === 'restart' ? 'requer restart' : 'aplica na hora'}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">{item.description}</Text>
          {item.value_type === 'bool' ? (
            <Switch checked={!!val} onChange={(e) => setVal(e.currentTarget.checked)} mt={4} />
          ) : item.value_type === 'int' ? (
            <NumberInput value={Number(val)} onChange={(v) => setVal(Number(v) || 0)} mt={4} w={220} />
          ) : (
            <TextInput value={String(val ?? '')} onChange={(e) => setVal(e.currentTarget.value)} mt={4} />
          )}
        </Stack>
        <Button size="xs" variant="light" loading={save.isPending} disabled={!dirty}
                onClick={() => save.mutate()}>Salvar</Button>
      </Group>
    </Card>
  )
}

function SecretCard({ secret }: { secret: SecretStatus }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [testing, setTesting] = useState(false)
  const [target, setTarget] = useState('')

  const save = useMutation({
    mutationFn: () => settingsApi.updateSecret(
      secret.name,
      // só envia campos preenchidos — não sobrescreve outros campos com '' (merge no backend)
      Object.fromEntries(Object.entries(fields).filter(([, v]) => v)) as Record<string, string>,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      notifications.show({ color: 'teal', title: 'Secret salvo', message: `${secret.name} atualizado.` })
      setEditing(false); setFields({})
    },
    onError: (e: unknown) => notifications.show({ color: 'red', title: 'Erro', message: err(e, 'Falha ao salvar secret.') }),
  })
  const runTest = useMutation({
    mutationFn: () => settingsApi.testSecret(secret.name, target),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      notifications.show({
        color: res.ok ? 'teal' : 'red',
        title: res.ok ? 'Teste OK' : 'Teste falhou',
        message: JSON.stringify(res.detail),
      })
      setTesting(false); setTarget('')
    },
    onError: (e: unknown) => notifications.show({ color: 'red', title: 'Erro', message: err(e, 'Falha no teste.') }),
  })

  return (
    <Card withBorder padding="sm">
      <Stack gap={4}>
        <Group justify="space-between">
          <Text size="sm" fw={600}>{secret.name}</Text>
          <Group gap="xs">
            <Badge size="xs" variant="light" color={secret.configured ? 'teal' : 'gray'}>
              {secret.configured ? 'configurado' : 'não configurado'}
            </Badge>
            {secret.last_test && (
              <Badge size="xs" variant="light" color={secret.last_test.ok ? 'teal' : 'red'}>
                último teste: {secret.last_test.ok ? 'ok' : 'falhou'}
              </Badge>
            )}
          </Group>
        </Group>
        <Text size="xs" c="dimmed">{secret.description}</Text>
        {secret.error && <Text size="xs" c="red">Secrets Manager indisponível.</Text>}
        <Group gap="xs">
          <Text size="sm" c="dimmed">Valor:</Text>
          <Text size="sm" ff="monospace">{secret.masked ?? '—'}</Text>
        </Group>

        {!editing ? (
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={() => setEditing(true)}>Alterar</Button>
            <Button size="xs" variant="subtle" leftSection={<IconTestPipe size={14} />}
                    onClick={() => setTesting((t) => !t)}>Testar</Button>
          </Group>
        ) : (
          <Stack gap={6}>
            {secret.fields.map((f) => (
              <PasswordInput
                key={f} label={f} placeholder="••••• (write-only)"
                data-sensitive="true" wrapperProps={{ 'data-sensitive': 'true' }}
                value={fields[f] ?? ''}
                onChange={(e) => setFields((prev) => ({ ...prev, [f]: e.currentTarget.value }))}
              />
            ))}
            <Group gap="xs">
              <Button size="xs" loading={save.isPending}
                      disabled={Object.values(fields).every((v) => !v)}
                      onClick={() => save.mutate()}>Salvar</Button>
              <Button size="xs" variant="subtle" onClick={() => { setEditing(false); setFields({}) }}>Cancelar</Button>
            </Group>
          </Stack>
        )}

        {testing && (
          <Group gap="xs" mt={4}>
            <TextInput size="xs" placeholder="Destino (nº WhatsApp ou e-mail)" style={{ flex: 1 }}
                       value={target} onChange={(e) => setTarget(e.currentTarget.value)} />
            <Button size="xs" loading={runTest.isPending} disabled={!target}
                    onClick={() => runTest.mutate()}>Enviar teste</Button>
          </Group>
        )}
      </Stack>
    </Card>
  )
}

function DbStatusTab() {
  const { data, isLoading } = useQuery({ queryKey: ['settings', 'db-status'], queryFn: () => settingsApi.dbStatus() })
  if (isLoading) return <Skeleton height={120} />
  if (!data) return <Alert color="red" title="Erro">Falha ao consultar o banco.</Alert>
  return (
    <Card withBorder padding="md">
      <Stack gap={6}>
        <Group gap="xs">
          <Badge color={data.connected ? 'teal' : 'red'} variant="light">
            {data.connected ? 'conectado' : 'sem conexão'}
          </Badge>
          {data.latency_ms != null && <Text size="sm" c="dimmed">latência {data.latency_ms} ms</Text>}
        </Group>
        <Text size="sm">Host: <Text span ff="monospace">{data.host}:{data.port}</Text></Text>
        <Text size="sm">Banco: <Text span ff="monospace">{data.database}</Text></Text>
        {data.error && <Text size="xs" c="red">{data.error}</Text>}
        <Text size="xs" c="dimmed">Somente leitura — credenciais ficam no deploy (bootstrap), não editáveis aqui.</Text>
      </Stack>
    </Card>
  )
}

export function Configuracoes() {
  const can = useCan()
  const canAccess = can('admin', 'platform')
  // useQuery sempre chamado (ordem de hooks estável); só dispara se tem acesso.
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    enabled: canAccess,
  })
  if (!canAccess) {
    return (
      <Alert color="yellow" title="Acesso restrito" icon={<IconAlertTriangle size={18} />}>
        Você não possui permissão para acessar as Configurações da plataforma.
      </Alert>
    )
  }

  const comunicacao = (data?.config ?? []).filter((c) => c.group === 'comunicacao')
  const operacional = (data?.config ?? []).filter((c) => c.group !== 'comunicacao')

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={2}>Configurações</Title>
        <Text c="dimmed" size="sm">
          Gestão centralizada de dados sensíveis e parâmetros da plataforma. Secrets são
          write-only (mascarados); credenciais de bootstrap do banco ficam fora desta tela.
        </Text>
      </Stack>

      {isLoading ? (
        <Stack gap="sm"><Skeleton height={120} /><Skeleton height={120} /></Stack>
      ) : (
        <Tabs defaultValue="comunicacao">
          <Tabs.List>
            <Tabs.Tab value="comunicacao" leftSection={<IconMail size={14} />}>Comunicação</Tabs.Tab>
            <Tabs.Tab value="operacional" leftSection={<IconSettings size={14} />}>Operacional</Tabs.Tab>
            <Tabs.Tab value="banco" leftSection={<IconDatabase size={14} />}>Banco</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="comunicacao" pt="md">
            <Stack gap="sm">
              {(data?.secrets ?? []).map((s) => <SecretCard key={s.name} secret={s} />)}
              {comunicacao.map((c) => <ConfigRow key={c.key} item={c} />)}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="operacional" pt="md">
            <Stack gap="sm">
              {operacional.map((c) => <ConfigRow key={c.key} item={c} />)}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="banco" pt="md"><DbStatusTab /></Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  )
}
